import Draggable from "react-draggable";
import { useState, useEffect, useRef } from "react";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import SummaryDetailModal from "./SummaryDetailModal";
import Swal from "sweetalert2";
import star from "../../../Artifacts/star.svg";
import { useSelector } from "react-redux";
import { axInst } from "../../Login/Service"; // ✅ axios instance

const SummaryModal = ({ handleModal }) => {
  const [showDetailSummaryModal, setShowDetailSummaryModal] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);

  const [inputs, setInputs] = useState({});
  const [dates, setDates] = useState({});
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignedTo, setAssignedTo] = useState({});
  const [resolvedDealerCode, setResolvedDealerCode] = useState([]); // ✅ final dealer_code

  const dropdownRef = useRef(null);

  const { data } = useSelector((e) => e.filtersDealerReview);

  useEffect(() => {
    if (data) {
      handleDealerFilter(data);
    }
  }, [data]);

  const handleDealerFilter = async (filters) => {
    const { dealer_name, dealer_code } = filters;

    // ✅ Case 1: Both empty
    if (
      (!dealer_name || dealer_name.length === 0) &&
      (!dealer_code || dealer_code.length === 0)
    ) {
      await Swal.fire({
        title: "Please select Dealer name or Dealer code",
        icon: "warning",
        confirmButtonText: "Close",
        customClass: {
          confirmButton:
            "bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md",
        },
      });
      handleModal(false);
      return false;
    }

    // ✅ Case 2: More than one dealer_code
    if (
      (dealer_code && dealer_code.length > 1) ||
      (dealer_name && dealer_name.length > 1)
    ) {
      await Swal.fire({
        title: "Please select 1 Dealer name or Dealer code",
        icon: "warning",
        confirmButtonText: "Close",
        customClass: {
          confirmButton:
            "bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md",
        },
      });
      handleModal(false);
      return false;
    }

    let pDealerCode = dealer_code || [];

    // ✅ Case 3: Only dealer_name present → fetch dealer code
    if (
      dealer_name &&
      dealer_name.length > 0 &&
      (!dealer_code || dealer_code.length === 0)
    ) {
      try {
        const { data: apiData } = await axInst.post(
          "dealer-review/dealer_review_filter",
          {
            dealer_name,
            dealer_code: [],
          }
        );

        const dealerNameArray =
          apiData?.DEALER_CODE?.buckets?.map((e) => e.key) || [];

        if (dealerNameArray.length > 0) {
          pDealerCode = [dealerNameArray[0]];
        }
      } catch (error) {
        Swal.fire("Error fetching dealer code").then(() => {
          handleModal(false);
        });
        return false;
      }
    }

    // ✅ Save resolved dealer_code
    setResolvedDealerCode(pDealerCode);

    return true;
  };

  // ---------------------------------------------------------

  // ✅ Fetch component list on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axInst.get(
          "dealer-summary-dashboard/collabax_component"
        );
        if (res?.data) {
          setOptions(res.data);
        }
      } catch (err) {
        console.error("Error fetching dropdown options:", err);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const fetchAssignOptions = async (resolvedDealerCode) => {
      try {
        const res = await axInst.post("dealer-summary-dashboard/assign_list", {
          dealer_code: resolvedDealerCode, // ✅ use resolved dealer code
        });

        if (Array.isArray(res?.data)) {
          setAssignOptions(res.data);
        } else {
          setAssignOptions([]);
        }
      } catch (err) {
        console.error("Error fetching Assign options:", err);
      }
    };

    if (selected.length > 0) {
      fetchAssignOptions(resolvedDealerCode);
    }
  }, [selected, resolvedDealerCode]);

  const toggleOption = (option) => {
    setSelected((prev) =>
      prev.includes(option.comp_id)
        ? prev.filter((id) => id !== option.comp_id)
        : [...prev, option.comp_id]
    );
  };

  // ✅ Only toggles detail modal visibility (doesn't close parent directly)
  const handleDetailModal = (status) => {
    setShowDetailSummaryModal(status);
  };

  const handleInputChange = (id, value) => {
    setInputs((prev) => ({ ...prev, [id]: value }));
  };

  const handleDateChange = (id, value) => {
    setDates((prev) => ({ ...prev, [id]: value }));
  };

  const handleAssignTo = (id, value) => {
    setAssignedTo((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async (resolvedDealerCode) => {
    const payload = selected.map((compId) => ({
      comp_id: compId,
      assign_to_user_id: assignedTo[compId] || null,
      summary_text: inputs[compId] || "",
      due_date: dates[compId]
        ? new Date(dates[compId]).toISOString().split("T")[0] + "T00:00:00Z"
        : "",
      priority_id: 3,
      dealer_code: Array.isArray(resolvedDealerCode)
        ? resolvedDealerCode[0]
        : resolvedDealerCode,
    }));

    try {
      const response = await axInst.post(
        "dealer-summary-dashboard/assign_summary",
        payload
      );

      if (response.status === 201) {
        await Swal.fire({
          title: "Summary saved successfully",
          icon: "success",
          confirmButtonText: "OK",
          didOpen: () => {
            const confirmBtn = document.querySelector(".swal2-confirm");
            if (confirmBtn) {
              confirmBtn.style.backgroundColor = "green";
              confirmBtn.style.color = "white";
            }
          },
        }).then((result) => {
          if (result.isConfirmed) {
            handleDetailModal(true);
          }
        });
      } else {
        throw new Error("Unexpected response message");
      }
    } catch (error) {
      console.error("Error saving summary:", error);
      await Swal.fire({
        title: "Failed to save summary",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {!showDetailSummaryModal ? (
        <Draggable handle=".modal-header" cancel="button" bounds="parent">
          <div className="bg-white rounded-lg shadow-lg w-96 relative h-[500px] flex flex-col">
            {/* Header */}
            <div className="modal-header cursor-move bg-gradient-to-r from-[#8463FA] to-[#EF6AE9] px-4 py-2 rounded-t-lg flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Action Plan</h2>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDetailModal(true);
                  }}
                  className="text-gray-800 hover:text-gray-600 rounded-xl font-semibold bg-white px-2 py-1 text-sm"
                >
                  Detail Summary
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModal(false);
                  }}
                  className="text-white hover:text-gray-200 rounded-full bg-[#E5E5E570] w-6 h-6"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 overflow-y-auto flex-1 scrollbar-none">
              {/* Dropdown */}
              <div className="relative w-full" ref={dropdownRef}>
                <button
                  onClick={() => setOpen(!open)}
                  className="w-full border border-[#BBBBBB] rounded-md px-3 py-2 text-left bg-white flex justify-between items-center"
                >
                  <span className="truncate text-[#8C8C8C]">
                    {selected.length > 0
                      ? selected.length + " Selected Components"
                      : "Select Components"}
                  </span>
                  <MdOutlineKeyboardArrowDown />
                </button>

                {open && (
                  <div className="absolute mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto z-50">
                    {options.map((option) => (
                      <label
                        key={option.comp_id}
                        className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(option.comp_id)}
                          onChange={() => toggleOption(option)}
                          className="mr-2"
                        />
                        {option.comp_name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2">
                {selected.length === 0 ? (
                  <div className="text-gray-500 text-center text-sm p-4 bg-[#D8D8D840] rounded-lg flex justify-center flex-col items-center mt-6">
                    <img
                      src={star}
                      alt="No Summary"
                      className="w-8 h-8 mb-2 opacity-80 "
                    />
                    <span>No Summary Yet.</span>
                    <span className="mt-2 block">
                      Select Components to get started!
                    </span>
                  </div>
                ) : (
                  selected.map((compId) => {
                    const option = options.find((o) => o.comp_id === compId);
                    if (!option) return null;

                    const selectedDate = dates[compId] || "";

                    return (
                      <div
                        key={compId}
                        className="border rounded-lg px-3 py-2 shadow-sm bg-white mb-3"
                      >
                        <div className="flex justify-between items-center mb-1">
                          <h3 className=" text-[15px] font-medium text-gray-700">
                            {option.comp_name}
                          </h3>
                        </div>

                        <textarea
                          className="w-full border rounded-md p-2  placeholder:text-sm "
                          rows="2"
                          placeholder={`Enter details for ${option.comp_name}`}
                          value={inputs[compId] || ""}
                          onChange={(e) =>
                            handleInputChange(compId, e.target.value)
                          }
                        />

                        <div className="flex gap-4 mt-1 w-full  ">
                          <div className="w-1/2 ">
                            <label className="text-sm font-medium text-gray-700">
                              Due Date
                            </label>
                            <input
                              type="date"
                              value={selectedDate}
                              onChange={(e) =>
                                handleDateChange(compId, e.target.value)
                              }
                              className="border rounded-md px-2 w-full text-sm h-[26px] text-gray-500 outline-none placeholder:text-xs"
                            />
                          </div>

                          <div className="w-1/2 ">
                            <label className="text-sm font-medium text-gray-700">
                              Select Assignee
                            </label>
                            <select
                              value={assignedTo[compId] || ""}
                              onChange={(e) =>
                                handleAssignTo(compId, e.target.value)
                              }
                              className="border rounded-md px-2  w-full outline-none text-sm h-[26px] text-gray-500 placeholder:text-xs "
                            >
                              <option value="">Select Assignee</option>

                              {assignOptions.map((opt, idx) => (
                                <option
                                  key={`${opt.USER_ID}-${idx}`}
                                  value={opt.USER_ID}
                                  title={`ID: ${opt.USER_ID} | Position: ${opt.PRIMARY_POSITION_TYPE}`} // 👈 hover text
                                >
                                  {opt.NAME}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Save Button */}
            {selected.length > 0 && (
              <div className="p-4 border-t">
                <button
                  onClick={() => handleSave(resolvedDealerCode)}
                  className="w-full bg-[#00A16C] text-white py-2 rounded-lg hover:opacity-90"
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </Draggable>
      ) : (
        <SummaryDetailModal handleModal={handleDetailModal} />
      )}
    </div>
  );
};

export default SummaryModal;
